import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DirtyGuardDialog } from "./DirtyGuardDialog";

const DirtyGuardContext = createContext(null);

// يمنع مغادرة أي نموذج غير محفوظ. يدعم عدة نماذج في الصفحة نفسها (خريطة مُسجّلين)،
// فيجمع حالة "متسخ" من الجميع ويحفظ كل النماذج المتسخة عند "حفظ وخروج".
export const DirtyGuardProvider = ({ children }) => {
  const navigate = useNavigate();
  const regs = useRef(new Map());
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const anyDirty = () => [...regs.current.values()].some((r) => r.dirty);
  const sync = () => {
    window.__dirtyGuard = { dirty: anyDirty() };
  };

  const register = useCallback((id, dirty, save) => { regs.current.set(id, { dirty, save }); sync(); }, []);
  const unregister = useCallback((id) => { regs.current.delete(id); sync(); }, []);

  const request = useCallback(
    (to) => {
      if (!anyDirty()) return navigate(to);
      setPending(to);
    },
    [navigate]
  );

  useEffect(() => {
    const onClick = (event) => {
      if (!anyDirty()) return;
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank") return;
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      event.preventDefault();
      event.stopPropagation();
      setPending(url.pathname + url.search);
    };
    const onBeforeUnload = (event) => {
      if (!anyDirty()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    document.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  const leave = (to) => {
    regs.current.clear();
    sync();
    setPending(null);
    navigate(to);
  };

  const saveAndLeave = async () => {
    setBusy(true);
    try {
      // لقطة ثابتة قبل التكرار: الحفظ قد يُعيد تسجيل النموذج في Map حيّة أثناء await = حلقة لا نهائية.
      const saves = [...regs.current.values()].filter((r) => r.dirty && r.save);
      for (const r of saves) await r.save();
      leave(pending);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DirtyGuardContext.Provider value={{ register, unregister, request }}>
      {children}
      <DirtyGuardDialog
        open={Boolean(pending)}
        busy={busy}
        onSave={saveAndLeave}
        onDiscard={() => leave(pending)}
        onCancel={() => setPending(null)}
      />
    </DirtyGuardContext.Provider>
  );
};

export const useDirtyGuard = (dirty, save) => {
  const ctx = useContext(DirtyGuardContext);
  const id = useRef(Symbol("dirty")).current;
  useEffect(() => {
    ctx?.register(id, dirty, save);
    return () => ctx?.unregister(id);
  }, [ctx, id, dirty, save]);
};

export const useDirtyNavigate = () => {
  const ctx = useContext(DirtyGuardContext);
  return (to) => ctx?.request(to);
};
